import React, { useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { gql, useQuery } from '@apollo/client';
import { Loader2 } from "lucide-react";

const GET_USER_DETAILS = gql`
   query GetUserDetails {
      getUserDetails {
         firstName
         lastName
         email
         phone
         address
      }
   }
`;

const BillingDetailsStep = ({ formData, setFormData, onNext, onBack }) => {
   const { loading, error, data } = useQuery(GET_USER_DETAILS);

   useEffect(() => {
      if (data?.getUserDetails) {
         const { firstName, lastName, email, phone, address } = data.getUserDetails;
         setFormData(prev => ({
            ...prev,
            fullName: `${firstName} ${lastName}`,
            email: email || '',
            phoneNumber: phone || '',
            address: address || ''
         }));
      }
   }, [data, setFormData]);

   const handleInputChange = (e) => {
      const { name, value } = e.target;
      setFormData(prev => ({
         ...prev,
         [name]: value
      }));
   };

   const handleSubmit = (e) => {
      e.preventDefault();
      onNext();
   };

   if (loading) {
      return (
         <Card className="w-full max-w-2xl mx-auto">
            <CardContent className="flex items-center justify-center p-8">
               <Loader2 className="h-8 w-8 animate-spin text-green-600" />
            </CardContent>
         </Card>
      );
   }

   return (
      <Card data-testid="billing-details-step" className="w-full max-w-2xl mx-auto">
         <CardHeader>
            <CardTitle className="text-center">Billing Details</CardTitle>
         </CardHeader>
         <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
               <div className="space-y-4">
                  <div>
                     <Label htmlFor="fullName">Full Name</Label>
                     <Input
                        id="fullName"
                        name="fullName"
                        value={formData.fullName || ''}
                        onChange={handleInputChange}
                        placeholder="Enter your full name"
                        autoComplete="name"
                        required
                     />
                  </div>

                  <div>
                     <Label htmlFor="email">Email Address</Label>
                     <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email || ''}
                        onChange={handleInputChange}
                        placeholder="Enter your email"
                        autoComplete="email"
                        required
                     />
                  </div>

                  <div>
                     <Label htmlFor="phoneNumber">Phone Number</Label>
                     <Input
                        id="phoneNumber"
                        name="phoneNumber"
                        value={formData.phoneNumber || ''}
                        onChange={handleInputChange}
                        placeholder="Enter your phone number"
                        autoComplete="tel"
                        required
                     />
                  </div>

                  <div>
                     <Label htmlFor="address">Complete Address</Label>
                     <Input
                        id="address"
                        name="address"
                        value={formData.address || ''}
                        onChange={handleInputChange}
                        placeholder="Enter your complete address"
                        autoComplete="street-address"
                        required
                     />
                  </div>
               </div>

               <div className="flex justify-between gap-4">
                  <Button
                     data-testid="back-button"
                     variant="outline"
                     onClick={onBack}
                  >
                     Back
                  </Button>
                  <Button
                     data-testid="next-button"
                     type="submit"
                     className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white"
                  >
                     Next
                  </Button>
               </div>
            </form>
         </CardContent>
      </Card>
   );
};

export default BillingDetailsStep;
